import React, { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  Trash2,
  Save,
  GripVertical,
  HelpCircle,
  Eye,
  EyeOff,
  Home,
  Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import { useAuthStore } from '../../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

interface FAQ {
  id: string;
  question: string;
  answer_md: string;
  category: string | null;
  sort_order: number;
  published: boolean;
  show_on_homepage: boolean;
  locale: string;
}

function SortableRow({ faq, onPatch, onSave, onDelete, dirty, saving }: {
  faq: FAQ;
  onPatch: <K extends keyof FAQ>(key: K, value: FAQ[K]) => void;
  onSave: () => void;
  onDelete: () => void;
  dirty: boolean;
  saving: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: faq.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-xl border border-gray-200 p-4 flex gap-3"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 self-start mt-1"
        title="Drag to reorder"
        type="button"
      >
        <GripVertical className="w-5 h-5" />
      </button>
      <div className="flex-1 space-y-3">
        <input
          type="text"
          value={faq.question}
          onChange={(e) => onPatch('question', e.target.value)}
          placeholder="Question..."
          className="w-full px-3 py-2 font-medium border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <textarea
          value={faq.answer_md}
          onChange={(e) => onPatch('answer_md', e.target.value)}
          rows={3}
          placeholder="Answer (markdown supported)..."
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono resize-y"
        />
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={faq.category || ''}
            onChange={(e) => onPatch('category', e.target.value || null)}
            placeholder="Category"
            className="px-2 py-1 text-xs border border-gray-300 rounded-md w-32"
          />
          <select
            value={faq.locale}
            onChange={(e) => onPatch('locale', e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 rounded-md"
          >
            <option value="th">Thai</option>
            <option value="en">English</option>
          </select>
          <label className="flex items-center gap-1.5 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={faq.published}
              onChange={(e) => onPatch('published', e.target.checked)}
              className="rounded border-gray-300 text-blue-600"
            />
            {faq.published ? <Eye className="w-3.5 h-3.5 text-green-600" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
            Published
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={faq.show_on_homepage}
              onChange={(e) => onPatch('show_on_homepage', e.target.checked)}
              className="rounded border-gray-300 text-blue-600"
            />
            <Home className="w-3.5 h-3.5 text-gray-500" />
            Homepage
          </label>
          <div className="flex-1" />
          {dirty && (
            <button
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save
            </button>
          )}
          <button
            onClick={onDelete}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-red-600 hover:bg-red-50 rounded-md"
          >
            <Trash2 className="w-3 h-3" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export const FAQList: React.FC = () => {
  const token = useAuthStore((s) => s.token);
  const [items, setItems] = useState<FAQ[]>([]);
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const authHeader = useMemo<Record<string, string>>(
    () => (token ? { Authorization: `Bearer ${token}` } : ({} as Record<string, string>)),
    [token]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchAll = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/admin/faqs`, { headers: authHeader });
      if (!res.ok) throw new Error();
      const json = await res.json();
      setItems(json.items || []);
    } catch {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const patchItem = <K extends keyof FAQ>(id: string, key: K, value: FAQ[K]) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [key]: value } : it)));
    setDirtyIds((prev) => new Set(prev).add(id));
  };

  const saveItem = async (faq: FAQ) => {
    setSavingIds((prev) => new Set(prev).add(faq.id));
    try {
      const res = await fetch(`${API_URL}/admin/faqs/${faq.id}`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: faq.question,
          answer_md: faq.answer_md,
          category: faq.category,
          published: faq.published,
          show_on_homepage: faq.show_on_homepage,
          locale: faq.locale,
        }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
      setDirtyIds((prev) => {
        const next = new Set(prev);
        next.delete(faq.id);
        return next;
      });
      toast.success('Saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(faq.id);
        return next;
      });
    }
  };

  const deleteItem = async (id: string) => {
    if (!confirm('Delete this FAQ?')) return;
    try {
      const res = await fetch(`${API_URL}/admin/faqs/${id}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      if (!res.ok) throw new Error();
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const createNew = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/faqs`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: 'New question?',
          answer_md: 'Type your answer here. Markdown is supported.',
          published: false,
        }),
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      setItems((prev) => [created, ...prev]);
      toast.success('Created');
    } catch {
      toast.error('Create failed');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((it) => it.id === active.id);
    const newIdx = items.findIndex((it) => it.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(items, oldIdx, newIdx);
    setItems(next);
    // Persist the new order. We fire-and-forget but show a toast on
    // failure so the user can refresh / retry.
    try {
      await fetch(`${API_URL}/admin/faqs/reorder`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: next.map((it) => it.id) }),
      });
    } catch {
      toast.error('Reorder failed — refresh to retry');
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
        <header className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <HelpCircle className="w-7 h-7 text-blue-600" />
              FAQs
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Rendered at <span className="font-mono text-gray-700">/faq</span> with FAQPage JSON-LD. Drag to reorder.
            </p>
          </div>
          <button
            onClick={createNew}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-sm"
          >
            <Plus className="w-4 h-4" />
            New FAQ
          </button>
        </header>

        {loading ? (
          <div className="text-center text-gray-500 py-10">Loading...</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl py-12 text-center text-gray-500">
            <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium text-gray-700">No FAQs yet</p>
            <p className="text-sm">Click "New FAQ" to add the first question.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {items.map((faq) => (
                  <SortableRow
                    key={faq.id}
                    faq={faq}
                    onPatch={(key, value) => patchItem(faq.id, key, value)}
                    onSave={() => saveItem(faq)}
                    onDelete={() => deleteItem(faq.id)}
                    dirty={dirtyIds.has(faq.id)}
                    saving={savingIds.has(faq.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </AdminLayout>
  );
};

export default FAQList;
